import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";

interface TruncateTooltipProps {
  text: string;
  displayText?: string;
  tooltipText?: string;
  maxWidth?: string;
  className?: string;
  tooltipClassName?: string;
  truncateWidth?: string;
  /** When true, use block/w-full/min-w-0 so truncation works inside table cells or flex children. Default false to avoid breaking inline usages. */
  fitContainer?: boolean;
  /** When true, show tooltip only when text is actually truncated. Default false so other components keep always-show behavior. */
  showTooltipOnlyWhenTruncated?: boolean;
}

export const TruncateTooltip: React.FC<TruncateTooltipProps> = ({
  text,
  displayText,
  tooltipText,
  maxWidth = "50%",
  className = "",
  tooltipClassName = "",
  truncateWidth = "max-w-[50%]",
  fitContainer = false,
  showTooltipOnlyWhenTruncated = false,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (nameRef.current && text) {
      const el = nameRef.current;
      if (showTooltipOnlyWhenTruncated) {
        const isTruncated = el.scrollWidth > el.clientWidth;
      }
      const rect = el.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 5,
        left: rect.left,
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const TooltipPortal: React.FC = () => {
    if (!showTooltip || !text) return null;

    const displayText = tooltipText || text;

    return ReactDOM.createPortal(
      <div
        className="fixed z-[9999] pointer-events-auto max-w-auto"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
      >
        <div
          className={`bg-white text-[#01187D] text-[0.875rem] font-lato font-medium rounded-lg shadow-lg border border-gray-200 py-2 px-2 break-words whitespace-normal ${tooltipClassName}`}
        >
          <span className="block">{displayText}</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <span
        ref={nameRef}
        className={`truncate font-lato font-normal ${truncateWidth} ${fitContainer ? "block w-full min-w-0" : ""} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ maxWidth }}
      >
        {text}
      </span>
      <TooltipPortal />
    </>
  );
};

export default TruncateTooltip;